/**
 * Copy for the composer's photo and poll surfaces and the shared game card
 * bubble (ISSUE-282, web half). The poll and game card strings below are
 * quoted verbatim from `.agents/design-prompts/53-chat-extras.md`'s own
 * `## Copy` section. Every component and every test that renders one of
 * these strings imports it from here, the same convention
 * `standaloneChatCopy.ts` already set.
 */

export const POLL_COMPOSE_TITLE = "New poll";
export const POLL_ADD_OPTION_LABEL = "Add option";
export const POLL_ALLOW_MULTIPLE_LABEL = "Let people pick more than one";
export const POLL_POST_LABEL = "Post poll";
export const POLL_MULTI_RESPONSE_NOTE = "Pick as many as you like.";
export const POLL_CHANGE_VOTE_LABEL = "Change your vote";
export const GAME_CARD_SHARED_CAPTION = "Shared from Scores";

/**
 * New strings, not quotations: the design brief names the poll question and
 * option fields but not their placeholder or remove-control copy. Plain, no
 * exclamation, no apology, no dash characters, per the `human-writing` rule.
 */
export const POLL_QUESTION_LABEL = "Question";
export const POLL_QUESTION_PLACEHOLDER = "Ask something";
export const POLL_OPTION_PLACEHOLDER = "Option";
export const POLL_REMOVE_OPTION_LABEL = "Remove option";
export const POLL_VOTE_FAILED_MESSAGE = "Couldn't record your vote. Try again.";

export const COMPOSER_PHOTO_ACTION_LABEL = "Add a photo";
export const COMPOSER_POLL_ACTION_LABEL = "Add a poll";
export const COMPOSER_GIF_ACTION_LABEL = "Add a GIF";
export const COMPOSER_REMOVE_PHOTO_LABEL = "Remove photo";
export const COMPOSER_PHOTO_UPLOAD_FAILED_LABEL = "Couldn't upload";
export const COMPOSER_PHOTO_RETRY_LABEL = "Retry";

/**
 * Up to 10 photos per message, each original file capped at 15 MB (Q205,
 * ANSWERS-16-batch-c-media.md line 139). The count ceiling is this
 * composer's own to enforce; the per-file byte ceiling is the same one
 * `useChatPhotoUpload.ts` already carries for a chat's own photo.
 */
export const COMPOSER_MAX_PHOTOS = 10;
export const COMPOSER_TOO_MANY_PHOTOS_MESSAGE = "Up to 10 photos per message.";

// ISSUE-1057. The composer's PDF attach action (C28) and the Files tab's empty-state guidance.
export const COMPOSER_ATTACH_ACTION_LABEL = "Add a photo, PDF or game";
export const COMPOSER_ATTACH_TITLE = "Attach";
export const COMPOSER_PDF_ACTION_LABEL = "Add a PDF";
export const COMPOSER_REMOVE_PDF_LABEL = "Remove PDF";
export const COMPOSER_PDF_UPLOADING_LABEL = "Uploading";
export const COMPOSER_PDF_NOT_A_PDF_MESSAGE =
  "That file isn't a PDF. Pick a PDF file.";
export const COMPOSER_PDF_EMPTY_MESSAGE =
  "That PDF is empty. Pick another file.";
export const COMPOSER_PDF_UPLOAD_NOT_RECEIVED_MESSAGE =
  "The upload didn't finish. Try again.";
export const COMPOSER_PDF_UPLOAD_FAILED_MESSAGE =
  "Couldn't upload that PDF. Try again.";
export const FILES_TAB_EMPTY_BODY = "PDFs shared in this chat show up here.";

/**
 * The shared photo bubble's own empty/failed frame (ISSUE-370): a photo that
 * never resolves, or that has since become unavailable, reads as this
 * neutral placeholder rather than a broken image icon or blank tile.
 */
export const PHOTO_UNAVAILABLE_LABEL = "Photo unavailable";

/**
 * ISSUE-750. The document row's own unresolved state: media-api is not
 * deployed in production yet (ISSUE-755), so `PdfBubble` and `FilesTab`
 * both draw this in place of an Open action whenever the object cannot be
 * resolved to a URL, rather than a blank row or a broken icon. Shared here
 * so both surfaces read the exact same word.
 */
export const PDF_UNAVAILABLE_LABEL = "Unavailable";

/**
 * ISSUE-750, U2. Mirrors messaging-api's own
 * `services/messaging-api/src/lib/pdfDocuments.ts` PDF_FILE_NAME_FALLBACK
 * literally: `/message/list` already substitutes this stand-in server side
 * whenever a pdf message has no stored filename, so `row.pdf` is null here
 * only for a malformed response, never for the ordinary no-filename case.
 * Kept as its own client-side constant rather than imported, because
 * apps/app has no shared package with messaging-api to import it from.
 */
export const PDF_FILE_NAME_FALLBACK = "PDF document";

/**
 * The GIF picker and its unavailable frame (R3, R4). The four strings below
 * are quoted verbatim from `design-prompts/53-chat-extras.md` lines 127, 129
 * and 147 to 148, and repeated in that file's own `## Copy` section at lines
 * 163 to 165. Do not reword them: the unavailable pair in particular is the
 * exact sentence `05-system-states.md` section 11 already named.
 */
export const GIF_SEARCH_PLACEHOLDER = "Search GIFs";
export const GIF_TRENDING_SHELF_LABEL = "Trending";
export const GIF_UNAVAILABLE_TITLE = "GIF search is unavailable right now";
export const GIF_UNAVAILABLE_BODY = "Try again in a bit.";
// ISSUE-1051's own string for a typed search with zero GIFs; not a quote from the brief.
export const GIF_NO_RESULTS_MESSAGE = "No GIFs match that search.";

/**
 * The provider credit mark that rides on every result tile. R3 calls this
 * mandatory and the brief's `## Anti-patterns` calls stripping it "a
 * contractual requirement, not a style choice", so no layout pressure
 * removes it.
 *
 * The mark names Giphy rather than reading a provider name off the
 * response because `/gif/search` does not return one, and because a result
 * set can only have come from Giphy anyway:
 * `services/messaging-api/src/lib/gifProvider/resolveActiveProvider.ts`
 * returns a provider for `messaging.gifProviderName === "giphy"` and `null`
 * for every other value, and a `null` resolve produces
 * `isProviderAvailable: false` with an empty item list. So there is no live
 * path today on which a tile renders bytes some other provider supplied.
 * When a second provider class lands, the response needs a provider name
 * field and this constant becomes a lookup.
 */
export const GIF_ATTRIBUTION_MARK = "GIPHY";

/**
 * The picker sheet's own title. Not a quotation: the brief specifies the
 * sheet's contents and its search placeholder but never names the sheet.
 */
export const GIF_PICKER_TITLE = "GIFs";

// ISSUE-1366. The web composer's emoji picker, paste, and drop.
export const COMPOSER_EMOJI_ACTION_LABEL = "Add an emoji";
export const COMPOSER_EMOJI_TITLE = "Emoji";
export const COMPOSER_DROP_PHOTOS_LABEL = "Drop photos to add them";

// SMOKE-5 (QA round 3). The message box's accessible name and its hint, the
// same words the phone composer uses. With photos staged the hint asks for a
// caption instead, as it does on the phone.
export const COMPOSER_INPUT_LABEL = "Message";
export const COMPOSER_INPUT_PLACEHOLDER = "Message";
export const COMPOSER_CAPTION_PLACEHOLDER = "Add a caption";
